import { Check, EyeOff, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { errMsg, http, photoSrc } from "@/lib/api";
import { Panel } from "@/components/host/HostOverview";

export function Toggle({ id, label, hint, checked, onChange }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <label htmlFor={id} className="cursor-pointer">
        <span className="block font-semibold">{label}</span>
        {hint && <span className="block text-sm text-[#3d5a51]">{hint}</span>}
      </label>
      <Switch id={id} checked={!!checked} onCheckedChange={onChange} className="data-[state=checked]:bg-[var(--emerald)]" data-testid={id} />
    </div>
  );
}

export default function HostPhotos({ photos, reload, eventData, setEventData }) {
  const ev = eventData?.event;
  const setFlag = async (k, v) => {
    try {
      const r = await http.put("/host/event", { [k]: v });
      setEventData(r.data);
      toast.success("Saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const approve = async (p, approved) => {
    await http.patch(`/host/photos/${p.id}`, { approved }).catch(() => {});
    reload();
  };
  const remove = async (p) => {
    if (!window.confirm("Remove this photo from the wall?")) return;
    await http.delete(`/host/photos/${p.id}`).catch(() => {});
    reload();
  };
  return (
    <div className="flex flex-col gap-6">
      {ev && (
        <Panel title="Photo settings">
          <Toggle id="toggle-photo-uploads" label="Allow guest photo uploads" checked={ev.photo_uploads_enabled} onChange={(v) => setFlag("photo_uploads_enabled", v)} />
          <Toggle id="toggle-photo-approval" label="Require my approval before photos appear" hint="Off = new photos publish immediately." checked={ev.photo_requires_approval} onChange={(v) => setFlag("photo_requires_approval", v)} />
        </Panel>
      )}
      <Panel title={`Photos (${photos.length})`}>
        {photos.length ? (
          <ul className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="host-photo-list">
            {photos.map((p) => (
              <li key={p.id} className="flex flex-col rounded-xl bg-[var(--ivory)] p-2 shadow-sm">
                <img src={photoSrc(p.id)} alt={p.caption || "Guest photo"} loading="lazy" className="aspect-square w-full rounded-lg object-contain bg-[var(--ivory-2)]" />
                <p className="mt-2 text-xs">{p.caption || <em>No caption</em>}{p.display_name && `, by ${p.display_name}`}</p>
                <span className={`mt-1 self-start rounded-full px-2 py-0.5 text-xs font-semibold ${p.approved ? "bg-[#DCE8E2]" : "bg-[#FCE9C8]"}`}>{p.approved ? "Visible" : "Awaiting approval"}</span>
                <div className="mt-2 flex gap-2">
                  {p.approved ? (
                    <button type="button" className="btn-ghost min-h-[40px] flex-1 px-2 text-xs" onClick={() => approve(p, false)}><EyeOff className="h-3.5 w-3.5" aria-hidden="true" /> Hide</button>
                  ) : (
                    <button type="button" className="btn-primary min-h-[40px] flex-1 px-2 text-xs" onClick={() => approve(p, true)} data-testid={`approve-photo-${p.id}`}><Check className="h-3.5 w-3.5" aria-hidden="true" /> Approve</button>
                  )}
                  <button type="button" className="btn-ghost min-h-[40px] px-3" onClick={() => remove(p)} aria-label="Delete photo"><Trash2 className="h-4 w-4" /></button>
                </div>
              </li>
            ))}
          </ul>
        ) : <p className="text-sm">No photos yet.</p>}
      </Panel>
    </div>
  );
}
